/**
 * Palette Extraction — SPA adapter.
 *
 * Browser-specific methods (extractFromImage, extractFromCamera) stay here.
 * Algorithm logic (k-means, median-cut, brand-safe filtering) is delegated
 * to @chromis/shared-logic.
 */

import {
  extractDominantColors,
  type RGBColor,
  type BrandSafeOptions,
  type ExtractionAlgorithm,
} from '@chromis/shared-logic';

export type { ExtractionAlgorithm, BrandSafeOptions } from '@chromis/shared-logic';

export class PaletteExtractor {
  async extractFromImage(
    imageData: ImageData,
    k: number = 5,
    algorithm: ExtractionAlgorithm = 'kmeans',
    brandSafeOptions?: BrandSafeOptions,
  ): Promise<string[]> {
    const pixels = this.getPixels(imageData);
    if (pixels.length === 0) return [];
    return extractDominantColors(pixels, k, algorithm, brandSafeOptions);
  }

  async extractFromCamera(
    k: number = 5,
    algorithm: ExtractionAlgorithm = 'kmeans',
    brandSafeOptions?: BrandSafeOptions,
  ): Promise<string[] | null> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });

      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();

      await new Promise((resolve) => {
        video.onloadedmetadata = resolve;
      });

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        stream.getTracks().forEach((track) => track.stop());
        return null;
      }

      ctx.drawImage(video, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      stream.getTracks().forEach((track) => track.stop());

      return await this.extractFromImage(imageData, k, algorithm, brandSafeOptions);
    } catch (error) {
      console.error('Camera access error:', error);
      return null;
    }
  }

  private getPixels(imageData: ImageData): RGBColor[] {
    const pixels: RGBColor[] = [];
    const data = imageData.data;
    const sampleRate = Math.max(1, Math.floor(data.length / (4 * 10000)));

    for (let i = 0; i < data.length; i += 4 * sampleRate) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];

      if (a > 128) {
        pixels.push({ r, g, b });
      }
    }

    return pixels;
  }
}

export const paletteExtractor = new PaletteExtractor();
